import React from "react";
import type {TableData} from "./ConstructionTableProps";
import ConstructionTableRow from "./ConstructionTableRow";
import type GuiValueDto from "../../../../dto/construction/GuiValueDto";
import {useSelector} from "react-redux";
import type {RootState} from "../../../../store";

interface ConstructionTableRowWithPossibleEmptyRowsProps {
  constructionId: string;
  constructionElementsName: string;
  constructionPartItems: TableData[];
  isAccepted: boolean;
}

const ConstructionTableRowWithPossibleEmptyRows: React.FunctionComponent<
  ConstructionTableRowWithPossibleEmptyRowsProps
> = ({constructionId, constructionElementsName, constructionPartItems, isAccepted}) => {
  if (constructionElementsName !== "footplates" && constructionElementsName !== "foundations") {
    throw new Error("ConstructionTableRowWithPossibleEmptyRows can only be used for footplates or foundations.");
  }

  const data = useSelector((state: RootState) => state.constructionNew.data);
  const mastCount = data?.constructionParts?.mastParts?.masts?.length ?? 0;

  let rowCounter = 1;

  const rows: React.ReactElement[] = constructionPartItems.flatMap(constructionPartItem => {
    const position = (constructionPartItem as TableData & {position: GuiValueDto<number>}).position;
    const rowsToAdd: React.ReactElement[] = [];

    // Add empty rows until we reach the position of the current item
    while (rowCounter < position.value) {
      rowsToAdd.push(
        <ConstructionTableRow
          constructionId={constructionId}
          constructionElementsName={constructionElementsName}
          constructionPartItem={constructionPartItem}
          key={rowCounter++}
          isAccepted={isAccepted}
          renderEmpty={true}
        />,
      );
    }
    // Add the actual item row
    rowsToAdd.push(
      <ConstructionTableRow
        constructionId={constructionId}
        constructionElementsName={constructionElementsName}
        constructionPartItem={constructionPartItem}
        key={rowCounter++}
        isAccepted={isAccepted}
      />,
    );

    return rowsToAdd;
  });

  const totalRows = rowCounter - 1;

  // Add empty rows if there are fewer rows than masts
  if (totalRows < mastCount) {
    const lastItem = constructionPartItems[constructionPartItems.length - 1];
    for (let i = 0; i < mastCount - totalRows; i++) {
      rows.push(
        <ConstructionTableRow
          constructionId={constructionId}
          constructionElementsName={constructionElementsName}
          constructionPartItem={lastItem}
          key={rowCounter++}
          isAccepted={isAccepted}
          renderEmpty={true}
        />,
      );
    }
  }

  return <tbody>{rows}</tbody>;
};

export default ConstructionTableRowWithPossibleEmptyRows;
